import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { addWorkshopTestimonial, editWorkshopTestimonial, getSingleWorkshopTestimonial } from "../../redux/services/workshopTestimonialSlice";
import { fileUpload } from "../../redux/services/fileUploadSlice";
import { ROUTES } from "../../constant";

const WorkshopTestimonialForm = ({ action }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const singleWorkshopTestimonial = useSelector((state) => state?.workshopTestimonial?.singleWorkshopTestimonial);
  
  const queryParams = new URLSearchParams(location.search);
  const testimonialId = queryParams.get("testimonialId");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const initialValues = {
    name: "",
    designation: "",
    description: "",
    rating: "",
    image: "",
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    designation: Yup.string().required("Designation is required"),
    description: Yup.string().required("Description is required"),
    rating: Yup.number().min(1).max(5).required("Rating is required (1-5)"),
    image: Yup.string().required("Image is required"),
  });

  const onSubmit = async (values, { resetForm }) => {
    setLoading(true);
    try {
      if (testimonialId) {
        values.webinarTestimonialId = testimonialId;
      }

      let response;
      if (testimonialId) {
        response = await dispatch(editWorkshopTestimonial(values));
      } else {
        response = await dispatch(addWorkshopTestimonial(values));
      }

      if (response?.payload?.status === 200) {
        toast.success(response?.payload?.message, {
          autoClose: 2000,
          pauseOnHover: false,
        });
        navigate(ROUTES.WORKSHOPS.TESTIMONIALS);
        resetForm();
      }
    } catch (error) {
      console.error("Error in saving testimonial:", error);
    } finally {
      setLoading(false);
    }
  };

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setValues } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  // Fetch single testimonial
  const getSingleRecord = useCallback(async () => {
    try {
        await dispatch(getSingleWorkshopTestimonial(testimonialId));
    } catch (error) {
      console.error("Error fetching single testimonial:", error);
    }
  }, [dispatch, testimonialId]);

  useEffect(() => {
    if (testimonialId) getSingleRecord();
  }, [testimonialId, getSingleRecord]);

  useEffect(() => {
    if (testimonialId && singleWorkshopTestimonial) {
      setValues({
        name: singleWorkshopTestimonial?.name ?? "",
        designation: singleWorkshopTestimonial?.designation ?? "",
        description: singleWorkshopTestimonial?.description ?? "",
        rating: singleWorkshopTestimonial?.rating ?? "",
        image: singleWorkshopTestimonial?.image ?? "",
      });
      setPreview(singleWorkshopTestimonial?.image ?? null);
    }
  }, [testimonialId, singleWorkshopTestimonial, setValues]);

  // Handle image upload
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const imageURL = URL.createObjectURL(file);
    setPreview(imageURL);

    const formData = new FormData();
    formData.append("image", file);

    try {
      setLoading(true);
      const response = await dispatch(fileUpload(formData));
      if (response) {
        const url = response.payload?.fileUrl || response.payload;
        setValues({ ...values, image: url });
        setLoading(false);
        toast.success("Image uploaded successfully!");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Image upload failed");
    }
  };

  return (
    <div>
      <div className="bg-background py-4 sm:py-4">
        <div className="px-4 md:py-10 py-5 bg-card shadow rounded-3xl sm:p-10">
          <p className="md:text-3xl text-xl text-center font-medium uppercase text-primary">{action} Workshop Testimonial</p>

          <form className="mt-6" onSubmit={handleSubmit}>
            <div className="grid md:grid-cols-2 grid-cols-1 gap-5">
              {/* Name */}
              <div className="md:col-span-1">
                <label className="text-sm font-medium text-foreground">Name</label>
                <input type="text" name="name" className="w-full px-4 py-2 border border-border rounded-xl bg-card" value={values.name} onChange={handleChange} onBlur={handleBlur} placeholder="Enter name" />
                {touched.name && errors.name && <div className="text-red-500 text-sm">{errors.name}</div>}
              </div>

              {/* Designation */}
              <div className="md:col-span-1">
                <label className="text-sm font-medium text-foreground">Designation</label>
                <input type="text" name="designation" className="w-full px-4 py-2 border border-border rounded-xl bg-card" value={values.designation} onChange={handleChange} onBlur={handleBlur} placeholder="Enter designation" />
                {touched.designation && errors.designation && <div className="text-red-500 text-sm">{errors.designation}</div>}
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-foreground">Description</label>
                <textarea name="description" rows={5} className="w-full px-4 py-2 border border-border rounded-xl bg-card" value={values.description} onChange={handleChange} onBlur={handleBlur} placeholder="Enter description" />
                {touched.description && errors.description && <div className="text-red-500 text-sm">{errors.description}</div>}
              </div>

              {/* Rating */}
              <div className="md:col-span-1">
                <label className="text-sm font-medium text-foreground">Rating (1-5)</label>
                <input type="number" name="rating" min="1" max="5" step={0.1} className="w-full px-4 py-2 border border-border rounded-xl bg-card" value={values.rating} onChange={handleChange} onBlur={handleBlur} placeholder="Enter rating" />
                {touched.rating && errors.rating && <div className="text-red-500 text-sm">{errors.rating}</div>}
              </div>

              {/* Image Upload */}
              <div className="md:col-span-1">
                <label className="text-sm font-medium text-foreground">Image</label>
                <input type="file" name="image" onChange={handleImageChange} className="w-full px-4 py-2 border border-border rounded-xl" />
                {touched.image && errors.image && <div className="text-red-500 text-sm">{errors.image}</div>}
                {preview && (
                  <div className="mt-3 flex items-center gap-3">
                    <p className="text-md text-foreground">Preview:</p>
                    <img src={preview} alt="preview" className="w-20 h-20 object-cover border border-border rounded-xl" />
                  </div>
                )}
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-center">
              <button type="submit" className="w-full sm:w-[250px] min-h-[50px] flex justify-center items-center text-white bg-primary text-lg font-semibold rounded-xl hover:bg-accent mt-7">
                {loading ? <div className="loader"></div> : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WorkshopTestimonialForm;
